<template>
  <div class="p-4 bg-white rounded-lg shadow-sm">
    <!-- Header -->
    <!-- Header -->
    <div
      class="flex flex-wrap justify-between items-center mb-4 border-b border-gray-200 pb-3 gap-3"
    >
      <!-- Left: Title -->
      <div>
        <h3 class="text-lg font-semibold text-gray-800 whitespace-nowrap">Entity Automations</h3>
      </div>

      <!-- Right: Controls -->
      <div class="flex items-center gap-3 justify-end">
        <!-- Try Using AI -->
        <!-- Need to implement AI feature here-->
        <!-- <div
          class="flex items-center cursor-pointer text-indigo-600 hover:text-indigo-700 transition-colors"
          @click="openActionTryUsingAIDialog"
        >
          <icons iconName="TryUsingAI" class="w-5 h-5 text-indigo-600" />
          <span class="ml-1 font-medium truncate">Try Using AI</span>
        </div> -->

        <!-- Search -->
        <el-input
          placeholder="Search automation..."
          v-model="search_string"
          prefix-icon="el-icon-search"
          size="small"
          clearable
          class="!w-[170px]"
          @clear="handleSearchClear"
        />

        <!-- Entity Selector -->
        <el-select
          v-if="!fromEntity"
          v-model="selected_entity_id"
          placeholder="Select Entity"
          filterable
          clearable
          class="!w-[170px] flex-shrink-0"
          @change="handleEntityChange"
        >
          <el-option
            v-for="entity in allEntities"
            :key="entity._id"
            :label="entity.name"
            :value="entity._id"
          />
        </el-select>

        <!-- Status Filter -->
        <el-select
          v-model="selectedType"
          placeholder="Filter by status"
          clearable
          class="!w-[170px] flex-shrink-0"
          @change="fetchEntityActions"
        >
          <el-option
            v-for="status in statusTypes"
            :key="status.value"
            :label="status.name"
            :value="status.value"
          />
        </el-select>

        <!-- Add Button -->
        <el-button
          type="primary"
          icon="el-icon-circle-plus-outline"
          class="!font-medium flex items-center gap-1"
          @click="handleAddNew"
        >
          New Automation
        </el-button>
      </div>
    </div>

    <!-- Data Table -->
    <div class="vue-data-table-default">
      <data-tables-server
        v-loading="loading || loadComponent"
        :element-loading-text="loadComponent ? loadText : loadingText"
        element-loading-spinner="el-icon-loading"
        :total="total"
        :data="entityActions"
        :current-page.sync="currentPage"
        :page-size="pageSize"
        :pagination-props="paginationProps"
        @query-change="loadData"
        @sort-change="sortChange"
      >
        <!-- Name -->
        <el-table-column label="Name" prop="name" width="200" sortable="custom">
          <template #default="{ row }">
            <el-tooltip :content="row.name || 'Unnamed Automation'">
              <span class="font-medium text-gray-800 truncate">
                {{ (row.name || 'Unnamed Automation') | truncate(20, '…') }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- Description -->
        <el-table-column label="Description" width="220">
          <template #default="{ row }">
            <span class="text-gray-500 text-sm">{{ row.description || '' }}</span>
          </template>
        </el-table-column>

        <!-- Entity -->
        <el-table-column
          v-if="!fromEntity"
          label="Entity"
          width="150"
          prop="entity_id"
          sortable="custom"
        >
          <template #default="{ row }">
            <span class="text-gray-700">{{ getEntityNameById(row.entity_id) || '-' }}</span>
          </template>
        </el-table-column>

        <!-- Trigger -->
        <el-table-column label="When" prop="when" min-width="120">
          <template #default="{ row }">
            <span class="text-gray-700">{{ getTriggerText(row.when) }}</span>
          </template>
        </el-table-column>

        <!-- Actions Count -->
        <el-table-column label="Actions" prop="then" min-width="150">
          <template #default="{ row }">
            <div v-if="row.then?.length" class="flex flex-col gap-0.5">
              <span class="text-xs text-gray-500">
                {{ getActionCount(row.then, 'ACTION') }} Actions
              </span>
              <span class="text-xs text-gray-500">
                {{ getActionCount(row.then, 'CONDITION') }} Conditions
              </span>
            </div>
            <span v-else class="text-xs italic text-gray-400">No actions</span>
          </template>
        </el-table-column>

        <!-- Created/Updated By -->
        <el-table-column label="Created By" prop="created_by" width="150">
          <template slot-scope="scope">
            {{ getUserData(scope.row.created_by) || '-' }}
          </template>
        </el-table-column>

        <el-table-column label="Updated By" prop="updated_by" width="150">
          <template slot-scope="scope">
            {{ getUserData(scope.row.created_by) || '-' }}
          </template>
        </el-table-column>

        <!-- Dates -->
        <el-table-column label="Created" prop="created_at" sortable="custom" min-width="120">
          <template #default="{ row }">
            <span class="text-xs text-gray-500">{{ row.created_at | globalDateTimeFormat }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Last Modified" prop="updated_at" sortable="custom" min-width="120">
          <template #default="{ row }">
            <span class="text-xs text-gray-500">{{ row.updated_at | globalDateTimeFormat }}</span>
          </template>
        </el-table-column>

        <!-- Edit/Delete -->
        <el-table-column label="Actions" fixed="right" width="120">
          <template slot-scope="scope">
            <div class="flex items-center gap-2">
              <el-button
                v-if="scope && checkHasPermission(scope.row)"
                plain
                type="warning"
                size="mini"
                class="!px-2"
                title="Edit"
                @click="handleEdit(scope.row)"
              >
                <i class="el-icon-edit"></i>
              </el-button>

              <el-button
                v-if="checkHasPermission(scope.row)"
                plain
                type="danger"
                size="mini"
                class="!px-2"
                title="Delete"
                @click="onDelete(scope.row)"
              >
                <i class="el-icon-delete"></i>
              </el-button>
            </div>
          </template>
        </el-table-column>

        <!-- Lock & Status -->
        <el-table-column fixed="right" width="140">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <el-switch
                v-model="row.status"
                active-color="#13ce66"
                inactive-color="#ff4949"
                active-value="ACTIVE"
                inactive-value="INACTIVE"
                :disabled="!checkHasPermission(row)"
                @change="handleStatusChange(row)"
              />
              <el-tooltip :content="getLockTooltip(row)" placement="left">
                <i
                  @click="handleLock(row)"
                  :class="[
                    row.is_locked ? 'el-icon-lock' : 'el-icon-unlock',
                    'cursor-pointer text-lg transition-transform hover:scale-110',
                    { 'opacity-50 pointer-events-none': !canShow(row) },
                  ]"
                  :style="{ color: row.is_locked ? '#ef4444' : '#22c55e' }"
                />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </data-tables-server>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { postAPICall } from '@/helpers/httpHelper';
  import axios from 'axios';
  import appConfig from '@/config/app';
  import templateBuilderHelper from '@/mixins/templateBuilderHelper';

  export default {
    name: 'AutomationList',
    mixins: [templateBuilderHelper],
    props: {
      currentEntity: { type: Object, default: null },
      fromEntity: { type: Boolean, default: false },
      loadComponent: { type: Boolean, default: false },
      loadText: { type: String, default: 'Loading, please wait...' },
      refreshKey: { type: Number, default: 0 },
    },
    data() {
      return {
        actionTryUsingAIDialog: false,
        loading: false,
        selected_entity_id: '',
        isGeneratingAction: false,
        entityActions: [],
        total: 0,
        currentPage: 1,
        pageSize: 10,
        page: 1,
        order_type: '',
        order_by: '',
        allEntities: [],
        selectedType: '',
        search_string: '',
        searchTimeout: null,
        usersData: {},
        aiPrompt: '',
        statusTypes: [
          { name: 'Active', value: 'ACTIVE' },
          { name: 'Inactive', value: 'INACTIVE' },
        ],
      };
    },
    computed: {
      ...mapGetters('navigationOpen', ['getIsMobile']),
      ...mapGetters('automationPipeline', [
        'getEntityActions',
        'getDeletePipelineStatus',
        'getUpdatePipelineStatus',
      ]),
      ...mapGetters('auth', ['getUserType', 'getActiveWorkspace', 'getAuthenticatedUser']),
      paginationProps() {
        return {
          pageSizes: [10, 20, 50],
          layout: 'total, sizes, prev, pager, next, jumper',
        };
      },
      loadingText() {
        return this.fromEntity
          ? `Fetching automations for ${this.currentEntity?.name || 'this entity'}...`
          : 'Fetching your automations...';
      },
      canShow() {
        return (row) =>
          this.getUserType.name === 'OWNER' || row.created_by === this.getAuthenticatedUser._id;
      },
    },
    watch: {
      search_string() {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.currentPage = 1;
          this.fetchEntityActions();
        }, 400);
      },
      currentEntity: {
        immediate: true,
        handler(entity) {
          if (entity && this.fromEntity) {
            this.selected_entity_id = entity._id;
            this.fetchEntityActions();
          }
        },
      },
      refreshKey() {
        this.fetchEntityActions();
      },
    },
    mounted() {
      this.initializeComponent();
    },
    methods: {
      async initializeComponent() {
        try {
          this.loading = true;
          if (!this.fromEntity) await this.fetchAllEntities();
          if (this.fromEntity && this.currentEntity)
            this.selected_entity_id = this.currentEntity._id;
          await this.fetchEntityActions();
        } finally {
          this.loading = false;
        }
      },
      async onDelete(data) {
        this.$confirm('Are you sure to delete permanently?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
        }).then(() => {
          this.deleteAction(data);
        });
      },
      async deleteAction(data) {
        this.loading = true;
        await this.$store.dispatch('automationPipeline/deletePipeline', data._id);
        if (this.getDeletePipelineStatus) {
          this.loading = false;
          this.$notify.success({
            title: 'Success',
            message: 'Pipeline deleted successfully',
          });
          this.fetchEntityActions();
        } else {
          this.loading = false;
          this.$notify.error({
            title: 'Error',
            message: 'Getting error while deleting',
          });
        }
      },
      getUserData(user) {
        if (!this.usersData[user]) {
          return '';
        }
        return this.usersData[user].first_name + ' ' + this.usersData[user].last_name;
      },

      async fetchAllEntities() {
        const response = await postAPICall('GET', '/entities/fetchAllEntities');
        this.allEntities = response?.data || [];
      },

      async fetchEntityActions() {
        this.loading = true;
        const params = {
          limit: this.pageSize,
          page: this.page,
          ...(this.selected_entity_id && { entity_id: this.selected_entity_id }),
          ...(this.order_by && { order_by: this.order_by }),
          ...(this.order_type && { order_type: this.order_type }),
          ...(this.search_string && { search_string: this.search_string }),
          ...(this.selectedType && { status: this.selectedType }),
        };
        await this.$store.dispatch('automationPipeline/getEntityActions', params);
        const result = this.getEntityActions;
        if (result) {
          this.entityActions = result.data || [];
          this.total = result.total || 0;
          await this.fetchUsersData();
        }
        this.loading = false;
      },

      async fetchUsersData() {
        const uniqueUsers = [
          ...new Set(
            this.entityActions.flatMap((e) => [e.created_by, e.updated_by].filter(Boolean))
          ),
        ];
        this.usersData = await this.fetchEntityUsersInfo(uniqueUsers, []);
      },

      // UI Helpers
      getEntityNameById(id) {
        return this.allEntities.find((e) => e._id === id)?.name || null;
      },
      getTriggerText(trigger) {
        if (['ADDED', 'DELETED'].includes(trigger)) return trigger;
        if (trigger.includes('template/')) {
          return (
            this.getFieldProperty(
              (this.currentEntity?.templates || []).map((e) => e.templateInfo) || [],
              trigger.replace('template/', ''),
              '_id',
              'name',
              'Template update'
            ) + ' template update'
          );
        }
        return trigger;
      },
      getFieldProperty(fields, key, keyProperty, property = '', defaultValue = '') {
        if (fields && fields.length && key && keyProperty) {
          let field = fields.find((e) => e && e[keyProperty] == key);
          if (field) {
            if (property) {
              return field[property] ? field[property] : defaultValue;
            }
            return field;
          }
          return defaultValue;
        }
        return defaultValue;
      },
      getActionCount(list, type) {
        return list.filter((a) => a.type === type).length;
      },
      getLockTooltip(a) {
        return a.is_locked ? 'Edit/delete restricted' : 'Anyone can edit/delete';
      },
      checkHasPermission(a) {
        return (
          !a.is_locked ||
          this.getUserType.name === 'OWNER' ||
          a.created_by === this.getAuthenticatedUser._id
        );
      },

      // CRUD
      handleAddNew() {
        this.$emit('add-automation');
      },
      handleEdit(a) {
        this.$emit('edit-automation', a);
      },
      async handleDelete(a) {
        try {
          await this.$confirm(`Delete "${a.name}"?`, 'Confirm', {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning',
          });
          await this.$store.dispatch('automationPipeline/deletePipeline', a._id);
          if (this.getDeletePipelineStatus) {
            this.$notify.success({
              title: 'Success',
              message: 'Deleted successfully',
            });
            this.fetchEntityActions();
          }
        } catch {
          this.$notify.error({
            title: 'Failed',
            message: 'Error deleting automation',
          });
        }
      },
      async handleStatusChange(a) {
        const original = a.status;
        try {
          await this.$store.dispatch('automationPipeline/updatePipeline', a);
          if (!this.getUpdatePipelineStatus) throw new Error();
          this.$notify.success({
            title: 'Success',
            message: `Automation ${a.status === 'ACTIVE' ? 'activated' : 'deactivated'}`,
          });
        } catch {
          a.status = original;
          this.$notify.error({
            title: 'Error',
            message: 'Failed to update status',
          });
        }
      },
      async handleLock(a) {
        if (!this.canShow(a)) return;
        const original = a.is_locked;
        a.is_locked = !a.is_locked;
        try {
          await this.$store.dispatch('automationPipeline/updatePipeline', a);
          this.$notify.success({
            title: 'Success',
            message: `Automation ${a.is_locked ? 'locked' : 'unlocked'}`,
          });
        } catch {
          a.is_locked = original;
          this.$notify.error({
            title: 'Error',
            message: 'Failed to update lock status',
          });
        }
      },

      // Sorting / Pagination
      async sortChange(sort) {
        this.order_by = sort.prop;
        this.order_type = sort.order === 'ascending' ? 'asc' : 'desc';
        this.fetchEntityActions();
      },
      async loadData(queryInfo) {
        if (queryInfo?.type !== 'init') {
          this.pageSize = queryInfo.pageSize || this.pageSize;
          this.page = queryInfo.page || this.page;
          this.fetchEntityActions();
        }
      },

      // AI
      async generateActionUsingAI() {
        if (!this.aiPrompt?.trim()) return this.$message.warning('Enter a description');
        this.isGeneratingAction = true;
        try {
          const payload = {
            user_query: this.aiPrompt,
            company_id: this.getActiveWorkspace.company_id,
            entity_name: this.selected_entity_id
              ? this.getEntityNameById(this.selected_entity_id)
              : '',
          };
          const res = await axios.post(`${appConfig.AI_BASE_DEV_URL}/entity_actions/`, payload);
          const actions = res?.data?.actions_response?.actions;
          if (actions?.length) {
            this.$emit('edit-automation', {
              ...actions[0],
              isAIGenerated: true,
              aiPrompt: this.aiPrompt,
              entity_id: this.selected_entity_id,
            });
            this.closeActionTryUsingAIDialog();
          } else {
            this.$message.warning('No automations generated');
          }
        } catch {
          this.$message.error('Failed to generate automation');
        } finally {
          this.isGeneratingAction = false;
        }
      },
      openActionTryUsingAIDialog() {
        this.actionTryUsingAIDialog = true;
        if (!this.aiPrompt) this.aiPrompt = 'Create an Automation for ';
      },
      closeActionTryUsingAIDialog() {
        this.actionTryUsingAIDialog = false;
        this.aiPrompt = '';
      },

      handleEntityChange() {
        this.currentPage = 1;
        this.fetchEntityActions();
      },
      handleSearchClear() {
        this.search_string = '';
        this.fetchEntityActions();
      },
    },
  };
</script>
